import { create } from 'zustand';

const BACKSTAGE_KEY = 'oneup.backstage';

function readBackstage(): boolean {
  try {
    return localStorage.getItem(BACKSTAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeBackstage(value: boolean): void {
  try {
    if (value) localStorage.setItem(BACKSTAGE_KEY, '1');
    else localStorage.removeItem(BACKSTAGE_KEY);
  } catch {
    /* ignore (private mode / disabled storage) */
  }
}

export type ActiveDrawer =
  | {
      type: 'task';
      id: string;
      name: string;
    }
  | {
      type: 'proposal';
      id: string;
      name: string;
    }
  | null;

export type TaskDrawerCommand = 'focus-edit' | 'quick-log' | 'send-update' | 'close-task';

export type CreateDrawerType = 'task' | 'proposal' | 'client' | 'contact' | 'website';

export interface CreateTaskInitial {
  name?: string;
  notes?: string;
  status?: string;
  priority?: string;
  dueDate?: string;
  clientId?: string;
  clientName?: string;
  websiteId?: string;
  websiteName?: string;
  source?: string;
  taskType?: string;
  sourceLabel?: string;
  sourceTitle?: string;
  gmailRecordId?: string;
  gmailUserId?: string;
}

export type CreateDrawerInitial =
  | {
      type: 'task';
      values?: CreateTaskInitial;
    }
  | {
      type: Exclude<CreateDrawerType, 'task'>;
    };

export interface CreateDrawerState {
  type: CreateDrawerType;
  nonce: number;
  values?: CreateTaskInitial;
}

export interface EmailComposerInitial {
  mode?: 'new' | 'reply';
  replyToRecordId?: string;
  threadId?: string;
  subject?: string;
  toEmails?: string[];
  clientId?: string;
  clientName?: string;
  userId?: string;
  userName?: string;
  taskId?: string;
  taskName?: string;
  websiteId?: string;
}

interface Notice {
  id: number;
  message: string;
  tone?: 'default' | 'destructive';
}

interface UiState {
  createDrawer?: CreateDrawerState;
  activeDrawer: ActiveDrawer;
  taskDrawerCommand?: { command: TaskDrawerCommand; nonce: number };
  notice?: Notice;
  commandPaletteOpen: boolean;
  commandPaletteInitialQuery: string;
  emailComposerOpen: boolean;
  emailComposerInitial?: EmailComposerInitial;
  copilotPanelOpen: boolean;
  copilotPanelInitialTaskId: string | undefined;
  copilotPanelInitialProposalId: string | undefined;
  copilotPanelSeed: { prompt: string; nonce: number } | undefined;
  voiceTrigger?: { nonce: number };
  backstage: boolean;
  openCreateDrawer: (initial: CreateDrawerInitial) => void;
  closeCreateDrawer: () => void;
  setActiveDrawer: (drawer: ActiveDrawer) => void;
  requestTaskDrawerCommand: (command: TaskDrawerCommand) => void;
  clearTaskDrawerCommand: (nonce?: number) => void;
  setNotice: (message: string, tone?: Notice['tone']) => void;
  clearNotice: (id: number) => void;
  openCommandPalette: (initialQuery?: string) => void;
  setCommandPaletteOpen: (open: boolean) => void;
  openEmailComposer: (initial?: EmailComposerInitial) => void;
  setEmailComposerOpen: (open: boolean) => void;
  openCopilotPanel: (initialTaskId?: string, initialProposalId?: string) => void;
  closeCopilotPanel: () => void;
  seedCopilotPanel: (prompt: string) => void;
  consumeCopilotPanelSeed: () => string | undefined;
  startVoiceInput: () => void;
  /** Toggles backstage mode (Konami). Returns the new value. */
  toggleBackstage: () => boolean;
}

export const useUiStore = create<UiState>((set, get) => ({
  createDrawer: undefined,
  activeDrawer: null,
  commandPaletteOpen: false,
  commandPaletteInitialQuery: '',
  emailComposerOpen: false,
  emailComposerInitial: undefined,
  copilotPanelOpen: false,
  copilotPanelInitialTaskId: undefined,
  copilotPanelInitialProposalId: undefined,
  copilotPanelSeed: undefined,
  backstage: readBackstage(),
  openCreateDrawer: (initial) =>
    set({
      createDrawer: {
        type: initial.type,
        nonce: Date.now(),
        values: initial.type === 'task' ? initial.values : undefined,
      },
    }),
  closeCreateDrawer: () =>
    set({
      createDrawer: undefined,
    }),
  setActiveDrawer: (drawer) => set({ activeDrawer: drawer }),
  requestTaskDrawerCommand: (command) =>
    set({ taskDrawerCommand: { command, nonce: Date.now() } }),
  clearTaskDrawerCommand: (nonce) => {
    const current = get().taskDrawerCommand;
    if (!current) return;
    if (nonce !== undefined && current.nonce !== nonce) return;
    set({ taskDrawerCommand: undefined });
  },
  setNotice: (message, tone = 'default') => set({ notice: { id: Date.now(), message, tone } }),
  clearNotice: (id) => {
    if (get().notice?.id === id) set({ notice: undefined });
  },
  openCommandPalette: (initialQuery = '') =>
    set({
      commandPaletteOpen: true,
      commandPaletteInitialQuery: initialQuery,
    }),
  setCommandPaletteOpen: (open) => set({ commandPaletteOpen: open }),
  openEmailComposer: (initial = {}) =>
    set({
      emailComposerOpen: true,
      emailComposerInitial: initial,
    }),
  setEmailComposerOpen: (open) =>
    set({
      emailComposerOpen: open,
      emailComposerInitial: open ? get().emailComposerInitial : undefined,
    }),
  openCopilotPanel: (initialTaskId, initialProposalId) =>
    set({
      copilotPanelOpen: true,
      copilotPanelInitialTaskId: initialTaskId,
      copilotPanelInitialProposalId: initialProposalId,
    }),
  closeCopilotPanel: () =>
    set({
      copilotPanelOpen: false,
      copilotPanelInitialTaskId: undefined,
      copilotPanelInitialProposalId: undefined,
      copilotPanelSeed: undefined,
    }),
  seedCopilotPanel: (prompt) =>
    set({ copilotPanelSeed: { prompt, nonce: Date.now() } }),
  consumeCopilotPanelSeed: () => {
    const seed = get().copilotPanelSeed;
    if (seed) set({ copilotPanelSeed: undefined });
    return seed?.prompt;
  },
  startVoiceInput: () => set({ voiceTrigger: { nonce: Date.now() } }),
  toggleBackstage: () => {
    const next = !get().backstage;
    writeBackstage(next);
    set({ backstage: next });
    return next;
  },
}));

export function isTypingTarget(target: EventTarget | null): boolean {
  const element = target as HTMLElement | null;
  return Boolean(
    element &&
      (element.tagName === 'INPUT' ||
        element.tagName === 'TEXTAREA' ||
        element.tagName === 'SELECT' ||
        element.isContentEditable ||
        element.closest('[contenteditable="true"]')),
  );
}
